import {Box, Paper, Skeleton, Tab, Tabs, Typography} from "@mui/material";
import Grid2 from "@mui/material/Unstable_Grid2";
import React, {useCallback, useEffect, useMemo, useState} from "react";
import useIsMounted from "../../../../../../hooks/use-is-mounted";
import StaticDashboardWidgetGenerator from "../../../../operator/analytics/dashboard-v2/static-widgets";
import axios from "axios";
import Api from "../../../../../../../core/services/api/api";
import DashboardWidget from "../../../../operator/analytics/dashboard-v2/components/dashboard-widget";
import {DateRangeButton} from "../../../../../../components/app-specific/date-range-controller";
import useQuery from "../../../../../../hooks/use-query";
import useStaticStatsWidgetsState, {
    STATIC_STATS_SAVED_STATE_KEYS,
} from "../../../../operator/analytics/dashboard-v2/hooks/use-static-stats-widgets-state";
import useAccessLevels from "../../../../../../hooks/use-access-levels";
import {NumberVisibilityModeProvider} from "../../../../operator/analytics/dashboard-v2/hooks/use-number-visibility-mode";
import NumberVisibilityModeToggle from "../../../../operator/analytics/dashboard-v2/components/number-visibility-mode-toggle";

const CustomerStatisticsSection = ({loading: loadingProps, customerCode, customerName,}) => {
    const [loadingStats, setLoadingStats] = useState(true);
    const [_widgets, setWidgets] = useState([]);
    const [customerInfo, setCustomerInfo] = useState(null);
    const isMounted = useIsMounted();
    const operatorAccessLevels = useAccessLevels();
    const canExportWidgets = Boolean(operatorAccessLevels.packages.dashboard.exportDashboardWidgets);

    const {
        loadingConfig,
        savingWidgetConfig,
        selectedCategory: savedSelectedCategory,
        setSelectedCategory: persistSelectedCategory,
        augmentCategorizedWidgets,
        saveWidgetOrderBy,
        saveWidgetConfig,
    } = useStaticStatsWidgetsState(STATIC_STATS_SAVED_STATE_KEYS.customer);

    const widgets = useMemo(() => augmentCategorizedWidgets(_widgets), [_widgets, augmentCategorizedWidgets]);
    const widgetCategories = useMemo(() => widgets.map(e => e.category), [widgets]);
    const [localSelectedCategory, setLocalSelectedCategory] = useState(undefined);

    const selectedStatCategory = useMemo(() => {
        if (localSelectedCategory && widgetCategories.includes(localSelectedCategory)) {
            return localSelectedCategory;
        }
        if (savedSelectedCategory && widgetCategories.includes(savedSelectedCategory)) {
            return savedSelectedCategory;
        }
        return widgetCategories[0];
    }, [localSelectedCategory, savedSelectedCategory, widgetCategories]);

    const selectedWidgets = useMemo(() => widgets.filter(e => e.category === selectedStatCategory).flatMap((e) => e.widgets), [widgets, selectedStatCategory]);

    const loading = useMemo(() => loadingProps || loadingStats || loadingConfig, [loadingProps, loadingStats, loadingConfig]);

    /**
     * Updates the selected category locally and persists it.
     */
    const handleCategoryChange = useCallback((_, value) => {
        setLocalSelectedCategory(value);
        void persistSelectedCategory(value);
    }, [persistSelectedCategory]);

    /**
     * Fetches the customer information widgets from the server.
     */
    const getCustomerInformationWidgets = useCallback(async (cancelToken) => {
        setLoadingStats(true);
        const widgets = await StaticDashboardWidgetGenerator.customerInformationView(cancelToken);
        if (!isMounted())
            return;
        setWidgets(widgets ?? []);
        if (!cancelToken?.reason)
            setLoadingStats(false);
    }, [isMounted]);

    /**
     * As soon as the component is mounted:
     * - Fetches the widgets.
     */
    useEffect(() => {
        const cancelTokenSource = axios.CancelToken.source();
        void getCustomerInformationWidgets(cancelTokenSource.token);
        return () => {
            cancelTokenSource.cancel();
        }
    }, [getCustomerInformationWidgets])

    /**
     * Fetches the customer information from the server.
     */
    const getCustomerInfo = useCallback(async (cancelToken) => {
        const response = await Api.autoCompleteCustomersV2(customerName, cancelToken);
        if (!isMounted())
            return;
        const found = response?.data.find(c => c.code === customerCode);
        setCustomerInfo(found);
    }, [isMounted, customerName, customerCode])

    /** As soon as the customer code or name changes, fetch the customer info */
    useEffect(() => {
        if (loadingProps)
            return;
        const cancelTokenSource = axios.CancelToken.source();
        void getCustomerInfo(cancelTokenSource.token);
        return () => {
            cancelTokenSource.cancel();
        }
    }, [getCustomerInfo, customerCode, customerName, loadingProps]);

    const [query, setQuery] = useQuery();
    const filters = useMemo(() => ({
        ...query,
        customers: !!customerInfo ? [JSON.stringify(customerInfo)] : [],
    }), [customerInfo, query]);

    /**
     * Defaults the date range in the query when missing.
     */
    useEffect(() => {
        setQuery((prev) => ({
            ...prev,
            ...(!('dateRangeValue' in prev) && {
                dateRangeValue: {
                    mode: 'auto',
                    auto: {type: 'current', batch: 'year', count: 1, untilNow: true},
                },
            }),
        }), {replace: true})
    }, [setQuery])

    return (
        <>
            <Paper sx={{padding: 2, display: 'flex', flexDirection: 'column', position: 'relative', height: '100%', gap: 3}}>
                <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2}}>
                    <Typography variant={'subtitle1'} fontWeight={600}>
                        Statistics
                    </Typography>
                    <Box sx={{display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1}}>
                        <NumberVisibilityModeToggle/>
                        <DateRangeButton
                            disallowedTypes={['next']}
                            disallowedBatches={['hour']}
                            disallowFromNow
                            value={filters.dateRangeValue}
                            onCommitted={(value) => {
                                setQuery((prevState) => ({
                                    ...prevState,
                                    dateRangeValue: value,
                                }), {replace: true})
                            }}
                        />
                    </Box>
                </Box>
                <Grid2 container spacing={2} sx={{width: '100%'}}>
                    {
                        loading &&
                        Array.from({length: 8}).map((_, index) => (
                            <Grid2 xs={12} lg={6} key={index}>
                                <Skeleton height={350} sx={{transform: 'none'}}/>
                            </Grid2>
                        ))
                    }
                    {
                        !loading &&
                        <>
                            <Grid2 xs={12}>
                                <Tabs variant='standard' value={selectedStatCategory} onChange={handleCategoryChange}>
                                    {
                                        widgetCategories.map((category) => (
                                            <Tab key={category} label={category} value={category}/>
                                        ))
                                    }
                                </Tabs>
                            </Grid2>
                            {
                                selectedWidgets.map((widget) => (
                                    <Grid2 {...widget?.size} key={widget.connectionId || widget.id}>
                                        <DashboardWidget
                                            simplified
                                            widget={widget}
                                            filters={filters}
                                            canViewWidgetFilters={false}
                                            canManageWidgetFilters={false}
                                            canExportWidgets={canExportWidgets}
                                            savingDashboardWidgetConfig={savingWidgetConfig}
                                            saveDashboardWidgetConfig={saveWidgetConfig}
                                            saveWidgetOrderBy={saveWidgetOrderBy}
                                        />
                                    </Grid2>
                                ))
                            }
                        </>
                    }
                </Grid2>
            </Paper>
        </>
    );
}

const CustomerStatisticsSectionWithNumberVisibility = (props) => (
    <NumberVisibilityModeProvider>
        <CustomerStatisticsSection {...props} />
    </NumberVisibilityModeProvider>
);

export default CustomerStatisticsSectionWithNumberVisibility;
